/**
 * Takes CRUD — Firestore: projects/{projectId}/takes/{takeId}
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  type Unsubscribe,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import type { Take, TakeType, TakeStatus } from "@/types";

function takesRef(projectId: string) {
  return collection(db, "projects", projectId, "takes");
}

// ─── Create ────────────────────────────────────────────────────

export interface CreateTakeInput {
  type: TakeType;
  title: string;
  duration_seconds: number;
  storage_path: string;
  format: string;
  mime_type: string;
  size_bytes: number;
  status: TakeStatus;
  order: number;
}

export async function createTake(projectId: string, input: CreateTakeInput): Promise<string> {
  const docRef = await addDoc(takesRef(projectId), {
    ...input,
    project_id: projectId,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  return docRef.id;
}

// ─── Update ────────────────────────────────────────────────────

export async function renameTake(projectId: string, takeId: string, title: string): Promise<void> {
  await updateDoc(doc(db, "projects", projectId, "takes", takeId), {
    title,
    updated_at: serverTimestamp(),
  });
}

export async function updateTakeStatus(
  projectId: string,
  takeId: string,
  status: TakeStatus,
  errorMessage?: string,
): Promise<void> {
  await updateDoc(doc(db, "projects", projectId, "takes", takeId), {
    status,
    ...(errorMessage ? { error_message: errorMessage } : {}),
    updated_at: serverTimestamp(),
  });
}

// ─── Delete ────────────────────────────────────────────────────

export async function deleteTake(projectId: string, takeId: string): Promise<void> {
  await deleteDoc(doc(db, "projects", projectId, "takes", takeId));
}

// ─── Real-time listener ────────────────────────────────────────

export function subscribeTakes(
  projectId: string,
  onData: (takes: Take[]) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  const q = query(takesRef(projectId), orderBy("order", "asc"));
  return onSnapshot(
    q,
    (snap) => {
      onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Take));
    },
    onError,
  );
}
